import "./DragAndDropFiles.css";
import { faCloudUploadAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import type { DropTargetMonitor } from "react-dnd";
import { convertFileToBase64 } from "@mirage/utilities/ConvertToBase64";
import { UploadedFile } from "@shared/mirage/core/contracts/UploadedFile";
import useAlertService from "@mirage/hooks/useAlertService";

export interface DragAndDropFilesProps {
    onDrop: (files: UploadedFile[]) => void;
    onClick: (files: UploadedFile[]) => void;
    label?: string;
    multiple?: boolean;
}

const DragAndDropFiles: React.FC<DragAndDropFilesProps> = memo(({ onDrop, onClick, label, multiple = true }) => {
    const showAlert = useAlertService();
    const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);

    const handleClick = () => {
        if (hiddenFileInput?.current) {
            hiddenFileInput.current.click();
        }
    };

    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: [NativeTypes.FILE],
            canDrop: (item: { files: UploadedFile[] }) => {
                if (!multiple && item.files.length > 1) {
                    showAlert("Only one file is allowed.", "error");
                    return false;
                }
                return true;
            },
            async drop(item: { files: UploadedFile[] }) {
                if (onDrop) {
                    const filesWithBase64 = await handleDropFile(item.files);
                    onDrop(filesWithBase64);
                }
            },
            collect: (monitor: DropTargetMonitor) => {
                return {
                    isOver: monitor.isOver(),
                    canDrop: monitor.canDrop(),
                };
            },
        }),
        [onDrop]
    );
    const dropOver = isOver;

    return (
        <Box className={`filesDropZone ${dropOver ? "filesDropZone--hover" : ""}`.trim()} ref={drop} onClick={handleClick}>
            <input multiple={multiple} type="file" ref={hiddenFileInput} onChange={attachmentsOnChangeHandler} className="filesDropZone__fileAppendInput"></input>
            <FontAwesomeIcon icon={faCloudUploadAlt} className="filesDropZone__icon" />
            <Typography variant="body2" className="filesDropZone__text">
                {label ? label : "Drop file or click to upload."}
            </Typography>
        </Box>
    );

    async function handleDropFile(files: UploadedFile[] | UploadedFile): Promise<UploadedFile[]> {
        if (Array.isArray(files)) {
            const convertedFiles = await Promise.all(files.map(async (file) => await convertFile(file)));
            return convertedFiles.filter((file) => file?.name && file.base64) as UploadedFile[];
        }
        const convertedFile = await convertFile(files);
        return convertedFile?.base64 ? ([convertedFile] as UploadedFile[]) : [];
    }

    async function attachmentsOnChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        const inputFiles = event.target.files;
        if (!inputFiles || inputFiles instanceof FileList === false || inputFiles.length === 0) {
            return;
        }
        if (multiple) {
            const files = hiddenFileInput.current?.files?.length ? Array.from(inputFiles) : [];
            const convertedFiles = await Promise.all(files.map(async (file) => await convertFile(file)));
            const filesWithBase64: UploadedFile[] = convertedFiles.filter((file) => file?.name && file.base64) as UploadedFile[];
            onClick(filesWithBase64);
        } else {
            const file = inputFiles?.item(0);
            const convertedFile = file && (await convertFile(file));
            const filesWithBase64 = convertedFile?.base64 ? ([convertedFile] as UploadedFile[]) : [];
            onClick(filesWithBase64);
        }
        if (hiddenFileInput.current) {
            hiddenFileInput.current.value = "";
        }
    }

    async function convertFile(file: UploadedFile | File) {
        let base64File = {} as UploadedFile;
        const base64 = await convertFileToBase64(file);
        if (base64 && typeof base64 === "string") {
            base64File = { ...file, name: file.name, base64: base64.slice(base64.indexOf(",") + 1) };
            return base64File;
        }
        showAlert(`Could not upload file - ${file.name}`, "error");
        return null;
    }
});

export default DragAndDropFiles;
